import { useRef, useEffect } from 'react'
import type { ReactNode } from 'react'
import { useValuationStore } from '@/store/valuationStore'
import { useUiStore } from '@/store/uiStore'
import { FieldInput } from './FieldInput'
import { PhotoStrip } from './PhotoStrip'
import { cn } from '@/utils/cn'
import type { SectionDefinition, FieldDefinition } from '@/types'
import { useConditionalFields } from '@/hooks/useConditionalFields'
import { getMessage } from '@/utils/messages'
import { FIELD_VALIDATORS } from '@/utils/fieldValidators'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'


interface SectionCardProps {
  section: SectionDefinition
  index: number
}

/** Inline guidance placeholder shown when a prerequisite selection is missing. */
function GuidanceMessage({ message }: { message: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
      <div className="w-10 h-10 rounded-full bg-[rgba(61,142,240,0.1)] border border-[rgba(61,142,240,0.25)] flex items-center justify-center text-[18px] flex-shrink-0">
        ℹ
      </div>
      <p className="text-[12px] text-[var(--text-muted)] max-w-[280px] leading-relaxed">
        {message}
      </p>
    </div>
  )
}

/**
 * A sub-header row that spans the full 2-column grid.
 * Renders a horizontal rule with a label to group related fields visually.
 */
function SubHeader({ label }: { label: string }) {
  return (
    // data-tour-subheader: lets the onboarding tour detect this divider as a preceding
    // sibling of a spotlighted field and include it in the highlight box, so its full-width
    // rule lines don't poke through the cutout when only the narrower field beneath is targeted.
    <div data-tour-subheader className="col-span-full flex items-center gap-3 pt-2 pb-1">
      {/* Left rule */}
      <div className="h-px flex-1 bg-[var(--border)]" />
      <span
        className={cn(
          'text-[14px] font-bold  whitespace-nowrap',
          'text-[var(--text-dim)] px-1',
          'flex items-center gap-[6px]',
        )}
        style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
      >
        {label}
      </span>
      {/* Right rule */}
      <div className="h-px flex-1 bg-[var(--border)]" />
    </div>
  )
}

/**
 * Renders the fields array, injecting a <SubHeader> whenever a field
 * carries a `subHeader` string. The sub-header takes the full col-span-2
 * row by being placed before the field inside the CSS grid.
 */
function FieldsGrid({
  fields,
  sectionId,
  errors,
}: {
  fields: FieldDefinition[]
  sectionId: string
  errors: Record<string, string>
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-[13px] gap-y-[13px]">
      {fields.map((field) => (
        <SubHeaderAwareField key={field.id} field={field} sectionId={sectionId} error={errors[field.id]} />
      ))}
    </div>
  )
}

/**
 * Renders an optional sub-header divider followed by the FieldInput.
 * Both are wrapped in a React Fragment so they slot directly into the
 * CSS grid without an extra wrapper element breaking the 2-col layout.
 */
function SubHeaderAwareField({
  field,
  sectionId,
  error,
}: {
  field: FieldDefinition
  sectionId: string
  error?: string
}) {
  return (
    <>
      {field.subHeader && <SubHeader label={field.subHeader} />}
      <FieldInput field={field} sectionId={sectionId} error={error} />
    </>
  )
}

export function SectionCard({ section }: SectionCardProps) {
  const { sectionStates, toggleSection, formValues, hasTriedSubmit, hasTriedSave } = useValuationStore()
  const { apiFieldErrors } = useUiStore()
  const cardRef = useRef<HTMLDivElement>(null)

  const state = sectionStates[section.id] ?? { expanded: false, complete: false, touched: false }
  const fields = useConditionalFields(section.id)
  const isExpanded = state.expanded
  const isComplete = false

  const vehicleType = formValues['basic-information']?.['vehicleType'] ?? ''

  // Step 1 — vehicle type not yet selected
  const needsVehicleType = section.id !== 'basic-information' && vehicleType === ''

  // Step 2 — vehicle type selected but body-information is still waiting for
  // the Use (Truck) or Type (Trailer) selection in Standard Equipment.
  // `fields` from useConditionalFields returns [] in that case.
  const useValue = formValues['standard-equipment']?.['use'] ?? ''
  const typeValue = formValues['standard-equipment']?.['type'] ?? ''
  const isValidUse = !!useValue && useValue !== 'Select' && useValue.toLowerCase() !== 'not in list'
  const isValidType = !!typeValue && typeValue !== 'Select' && typeValue.toLowerCase() !== 'not in list'
  const hasUseOrType = vehicleType === 'Truck/Tractor' ? isValidUse : vehicleType === 'Trailer' ? isValidType : false

  const needsUseOrType =
    !needsVehicleType &&
    section.id === 'body-information' &&
    vehicleType !== '' &&
    fields.length === 0 &&
    !hasUseOrType

  // Step 3 — Use/Type is selected but the attribute-driven fields (and any
  // Subtype lookup) are still being fetched from the backend.
  const isLoadingBodyInfo =
    !needsVehicleType &&
    !needsUseOrType &&
    section.id === 'body-information' &&
    hasUseOrType &&
    fields.length === 0

  // Derive the label shown in message 2 ("Use" vs "Type")
  const useTypeLabel = vehicleType === 'Truck/Tractor' ? 'Use' : 'Type'

  // API field errors for this section
  const sectionApiErrors = apiFieldErrors[section.id] ?? {}

  // Only consider API errors for fields that are currently visible
  const visibleFieldIds = new Set(fields.map((f) => f.id))
  const hasVisibleApiError = Object.keys(sectionApiErrors).some((fieldId) => visibleFieldIds.has(fieldId))

  // True when submit was attempted and this section has at least one unfilled required field,
  // or when the API returned field-level errors for visible fields in this section
  const sectionValues = formValues[section.id] ?? {}
  const SAVE_REQUIRED_FIELDS = new Set(['claimRefNo', 'vehicleType'])
  // Honors FieldDefinition.formSection (e.g. the Use/Type Subtype field, rendered in
  // body-information but stored alongside Use/Type in standard-equipment).
  const fieldVal = (f: FieldDefinition) => formValues[f.formSection ?? section.id]?.[f.id] ?? ''
  const hasSectionError =
    (hasTriedSubmit && fields.some(
      (f) => f.required && (!fieldVal(f) || fieldVal(f).startsWith('Select') || !!FIELD_VALIDATORS[f.id]?.(fieldVal(f), sectionValues))
    )) ||
    (hasTriedSave && fields.some(
      (f) => SAVE_REQUIRED_FIELDS.has(f.id) && (!fieldVal(f) || fieldVal(f).startsWith('Select'))
    )) ||
    hasVisibleApiError

  // Smooth-scroll to section header when it opens
  useEffect(() => {
    if (isExpanded && cardRef.current) {
      const timer = setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [isExpanded])

return (
    <div
      ref={cardRef}
      id={`section-${section.id}`}
      className={cn(
        'bg-[var(--surface)] border border-[var(--border)] rounded-[var(--radius)]',
        'mb-3 overflow-hidden transition-[border-color] duration-200',
        isExpanded && 'border-[var(--border-active)]',
        isComplete && !isExpanded && 'border-[rgba(34,197,94,0.4)]',
        hasSectionError && !isExpanded && 'border-[var(--error)]'
      )}
      style={{ scrollMarginTop: '72px' }}
    >
      {/* ── Collapsed / Expanded header ───────────────────────── */}
      <div
        onClick={() => toggleSection(section.id)}
        className={cn(
          'flex items-center gap-[14px] px-5 py-[15px]',
          'cursor-pointer select-none transition-[background] duration-150',
          'hover:bg-[rgba(0,0,0,0.02)] section-head'
        )}
      >
        {/* Section icon */}
        <div
          className={cn(
            'w-9 h-9 rounded-[9px] flex items-center justify-center text-[17px] flex-shrink-0',
            'border transition-all duration-200',
            isExpanded && 'bg-[var(--accent)] border-[var(--accent)] text-white',
            isComplete && 'bg-[rgba(34,197,94,0.1)] border-[var(--success)]',
            hasSectionError && !isExpanded && 'bg-[rgba(220,38,38,0.1)] border-[var(--error)] text-[var(--error)]'
          )}
          style={!isExpanded && !isComplete && !hasSectionError
            ? { backgroundColor: `${section.iconColor}1f`, borderColor: `${section.iconColor}66`, color: section.iconColor }
            : (!isExpanded && !hasSectionError ? { color: section.iconColor } : undefined)
          }
        >
          {section.icon}
        </div>

        {/* Section name */}
        <span
          className={cn(
            'text-[14px] font-bold flex-1',
            hasSectionError && !isExpanded ? 'text-[var(--error)]' : 'text-[var(--text)]'
          )}
          style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
        >
          {section.name}
        </span>

        {/* Completion ribbon */}
        {isComplete && (
          <span
            className={cn(
              'text-[9px] text-[var(--success)]',
              'border border-[rgba(34,197,94,0.4)] rounded-[10px]',
              'px-2 py-[2px] tracking-[1px] uppercase'
            )}
          >
            {getMessage('section_card.badge_done')}
          </span>
        )}


        {/* Expand / collapse chevron */}
        <span
          className={cn(
            'text-[var(--text-muted)] text-[20px] flex-shrink-0 self-start mt-[3px]',
            'transition-transform duration-[280ms]',
            isExpanded && 'rotate-180'
          )}
        >
          ▾
        </span>
      </div>

      {/* ── Expanded body ─────────────────────────────────────── */}
      {isExpanded && (
        <div className="animate-slide-down px-4 sm:px-5 pb-5 border-t border-[var(--border)]">
          {needsVehicleType ? (
            <GuidanceMessage
              message={<>{getMessage('section_card.vehicle_type_prompt_pre')} <strong className="text-[var(--text-dim)]">{getMessage('section_card.vehicle_type_label')}</strong> {getMessage('section_card.vehicle_type_prompt_mid')} <strong className="text-[var(--text-dim)]">{getMessage('section_card.basic_info_label')}</strong> {getMessage('section_card.vehicle_type_prompt_post')}</>}
            />
          ) : needsUseOrType ? (
            <GuidanceMessage
              message={<>{getMessage('section_card.use_type_prompt_pre')} <strong className="text-[var(--text-dim)]">{useTypeLabel}</strong> {getMessage('section_card.use_type_prompt_mid')} <strong className="text-[var(--text-dim)]">{getMessage('section_card.standard_equip_label')}</strong> {getMessage('section_card.use_type_prompt_post')}</>}
            />
          ) : isLoadingBodyInfo ? (
            <div className="py-8">
              <LoadingSpinner size="md" label={getMessage('section_card.body_info_loading')} />
            </div>
          ) : (
            <>
              {/* Photo / document capture strip — hidden for basic-information & vehicle-condition */}
              {section.id !== 'basic-information' &&
              section.id !== 'vehicle-condition' &&

                <PhotoStrip sectionId={section.id} hasVisibleFields={fields.length > 0} />
              }

              {/* Form fields with inline sub-header dividers */}
              <FieldsGrid fields={fields} sectionId={section.id} errors={sectionApiErrors} />
            </>
          )}

          {/* Mark complete action */}
          {/* <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={handleMarkComplete}
              disabled={isComplete}
              className={cn(
                'flex items-center gap-2 px-4 py-[9px] rounded-[var(--radius-sm)]',
                'font-mono text-[12px] font-medium',
                'border transition-all duration-[180ms]',
                isComplete
                  ? 'bg-[var(--section-complete)] border-[rgba(34,197,94,0.4)] text-[var(--success)] cursor-default'
                  : 'bg-[var(--surface2)] border-[var(--border)] text-[var(--text-dim)] cursor-pointer hover:border-[var(--accent3)] hover:text-[var(--accent3)] hover:bg-[rgba(13,168,106,0.05)]'
              )}
            >
              {isComplete ? '✓ Complete' : '✓ Mark Complete'}
            </button>
          </div> */}
        </div>
      )}
    </div>
  )
}
